import { EMPTY_INTERACTIVE_OBJECTS } from "./chartState";

// Chart selectors
export const chartSelectors = (get) => ({
  getChart: (id) => {
    const chart = get().charts.find((c) => c.id === id);
    return chart;
  },

  // Data is now stored separately in chartData map for performance
  // This prevents Workspace re-renders on every quote update
  getData: (chartId) => {
    return get().chartData[chartId]?.data;
  },

  getDataVersion: (chartId) => {
    return get().chartData[chartId]?.dataVersion ?? 0;
  },

  getPendingInvalidation: (chartId) => {
    return get().chartData[chartId]?.pendingInvalidation ?? false;
  },

  getSelectedChart: () => {
    const { focusedChartId, charts, selectedTab } = get();
    if (focusedChartId) {
      const focused = charts.find((c) => c.id === focusedChartId);
      if (focused) return focused;
    }
    if (selectedTab === null) return null;
    return charts[selectedTab];
  },

  getSelectedChartPeriodicity: () => {
    const chart = get().getSelectedChart();
    return chart ? chart.periodicity : "1D";
  },

  // Interactive objects selectors
  getInteractiveObjects: (chartId, chartSectionId = null) => {
    const chart = get().getChart(chartId);
    const key = chartSectionId ? `${chartId}_${chartSectionId}` : chartId;
    return chart?.interactiveObjects?.[key] || EMPTY_INTERACTIVE_OBJECTS;
  },

  getActiveInteractive: (chartId) => {
    return get().activeInteractiveTools[chartId] || null;
  },

  // Section management selectors
  getSectionOrder: (chartId) => {
    const chart = get().getChart(chartId);
    return chart?.sectionOrder || null;
  },

  getSectionHeights: (chartId) => {
    const chart = get().getChart(chartId);
    return chart?.sectionHeights || null;
  },

  getDefaultSectionOrder: (chartId) => {
    const chart = get().getChart(chartId);
    if (!chart) return [];

    // Create default order: main chart first (index 0), then indicators in order
    const separateIndicators =
      chart.indicators?.filter((ind) => ind.requireNewAxis) || [];

    return [0, ...separateIndicators.map((_, i) => i + 1)]; // 0 = main, 1+ = indicators
  },

  getDefaultSectionHeights: (chartId) => {
    const chart = get().getChart(chartId);
    if (!chart) return {};

    // Create default heights: main chart gets more space, indicators get equal smaller portions
    const separateIndicators =
      chart.indicators?.filter((ind) => ind.requireNewAxis) || [];
    const totalSections = 1 + separateIndicators.length; // main + indicators

    if (totalSections === 1) {
      return { 0: 1.0 }; // Only main chart gets 100%
    }

    // Main chart gets 60%, indicators split the remaining 40%
    const mainHeight = 0.6;
    const indicatorHeight = 0.4 / separateIndicators.length;

    const heights = { 0: mainHeight }; // Main chart
    separateIndicators.forEach((_, i) => {
      heights[i + 1] = indicatorHeight; // Indicator charts
    });

    return heights;
  },
});
